import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMenu, MenuItem } from '../../hooks/useMenu';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { SignOutButton } from '../auth/SignOutButton';
import { isSidebarRouteActive } from '../../utils/sidebarRouteMatch';
import { AppBrandMark } from './AppBrandMark';
import { useAppLogo } from '../../hooks/useAppLogo';

interface DynamicSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const DynamicSidebar: React.FC<DynamicSidebarProps> = ({ isOpen = true, onClose }) => {
  const { menu, loading, error } = useMenu();
  const { t } = useLang();
  const { user } = useAuth();
  const logo = useAppLogo(true);
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const toggleMenu = (menuTitle: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuTitle) 
        ? prev.filter(item => item !== menuTitle)
        : [...prev, menuTitle]
    );
  };

  const isActive = (path: string) => isSidebarRouteActive(location.pathname, path);
  
  const isMenuActive = (menuItem: MenuItem): boolean => {
    if (menuItem.route && isActive(menuItem.route)) return true;
    if (menuItem.children) {
      return menuItem.children.some(child => child.route && isActive(child.route));
    }
    return false;
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.code);
    const isItemActive = isMenuActive(item);

    if (hasChildren) {
      return (
        <li key={item.id}>
          <button
            onClick={() => toggleMenu(item.code)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50 ${
              isItemActive
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="flex-shrink-0">
                {getIconForMenu(item.code)}
              </div>
              <span className="truncate text-left whitespace-nowrap">{item.name || t(item.code)}</span>
            </div>
            <div className="flex-shrink-0">
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          
          {isExpanded && (
            <ul className="mt-2 ml-8 space-y-1">
              {item.children?.map((child) => (
                <li key={child.id}>
                  <Link
                    to={child.route}
                    className={`flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-colors hover:bg-gray-50 ${
                      child.route && isActive(child.route)
                        ? 'bg-gradient-to-r from-cyan-400 to-blue-400 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {getIconForMenu(child.code)}
                    </div>
                    <span className="truncate whitespace-nowrap">{child.name || t(child.code)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.id}>
        <Link
          to={item.route}
          className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors hover:bg-gray-50 ${
            item.route && isActive(item.route)
              ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <div className="flex-shrink-0">
            {getIconForMenu(item.code)}
          </div>
          <span className="truncate whitespace-nowrap">{item.name || t(item.code)}</span>
        </Link>
      </li>
    );
  };

  const getIconForMenu = (code: string) => {
    // Icon mapping based on function codes
    const iconMap: { [key: string]: string } = {
      // PHÍ DỊCH VỤ (Service Fees)
      'SERVICE_PAYMENT': '💳',
      'TRANSACTION_REPORT': '📊',
      'RECEIVABLE_REPORT': '💰',
      'SERVICE_FEE_MANAGEMENT': '📋',

      // QUẢN LÝ HỢP ĐỒNG
      'CONTRACT_MANAGEMENT': '📄',
      'REVENUE_CONTRACTS': '📈',
      'EXPENDITURE_CONTRACTS': '📉',

      // QUẢN LÝ THU CHI (Income & Expenditure)
      'IE_REVENUE_ACTIVITIES': '💵',
      'IE_EXPENDITURE_ACTIVITIES': '💸',
      'IE_SUMMARY': '📊',
      'IE_BANK_ACCOUNTS': '🏦',
      'IE_FINANCIAL_REPORTS': '📈',
      'IE_DATA_IMPORT': '📥',
      
      // QUẢN LÝ CHÍNH (Main Management)
      'DASHBOARD': '📈',
      
      // QUẢN LÝ TÀI KHOẢN (Account Management)
      'ACCOUNT_LIST': '👤',
      'ACCOUNT_ADD': '➕',
      'ACCOUNT_EDIT': '✏️',
      'PERMISSION_GROUPS': '🔐',
      'PERMISSION_GROUP_ADD': '➕',
      'PERMISSION_GROUP_EDIT': '✏️',
      
      // QUẢN LÝ CHUNG CƯ (Building Management)
      'BUILDING_LIST': '🏢',
      'BUILDING_ADD': '➕',
      'BUILDING_EDIT': '✏️',
      'SEND_FEE_NOTIFICATION': '🔔',
      'FEE_CATEGORIES': '📂',
      'FEE_CATEGORY_ADD': '➕',
      'FEE_CATEGORY_EDIT': '✏️',
      'BULK_FEE_ENTRY': '📥',
      'EXPORT_PDF_SERVICE_FEES': '📄',
      'PDF_TEMPLATES': '📋',
      'BUILDING_BANNER_LIST': '🖼️',
      'BUILDING_BANNER_ADD': '➕',
      'BUILDING_BANNER_EDIT': '✏️',
      'BUILDING_NEWS_LIST': '📰',
      'BUILDING_NEWS_ADD': '➕',
      'BUILDING_NEWS_EDIT': '✏️',
      
      // QUẢN LÝ CĂN HỘ (Apartment Management)
      'APARTMENT_SEARCH': '🔍',
      'APARTMENT_LIST': '🏠',
      'APARTMENT_ADD': '➕',
      'APARTMENT_EDIT': '✏️',
      'APARTMENT_DETAILS': '📋',
      'APARTMENT_GROUPS': '🏘️',
      'APARTMENT_USAGE_TYPES': '🏷️',
      'RESIDENT_LIST': '👥',
      'MONTHLY_VEHICLE_MANAGEMENT': '🅿️',
      'CARD_MANAGEMENT': '💳',
      
      // QUẢN LÝ CÔNG VIỆC (Task Management)
      'TASK_ADD': '➕',
      'TASK_LIST': '📋',
      'TASK_INCOMPLETE': '⏳',
      'TASK_COMPLETED': '✅',
      'TASK_DETAILS': '📋',
      
      // TIỆN ÍCH TÒA NHÀ (Building Utilities)
      'BUILDING_UTILITY_GROUPS': '🏢',
      'BUILDING_UTILITY_GROUP_ADD': '➕',
      'BUILDING_UTILITY_GROUP_EDIT': '✏️',
      'BUILDING_UTILITY_LIST': '🏠',
      'BUILDING_UTILITIES': '🏢',
      'BUILDING_UTILITY_ADD': '➕',
      'BUILDING_UTILITY_EDIT': '✏️',
      'BUILDING_UTILITY_BOOKINGS': '📅',
      'BUILDING_UTILITY_BOOKING_ADD': '➕',
      'BUILDING_UTILITY_BOOKING_EDIT': '✏️',
      
      // DỊCH VỤ TÒA NHÀ (Building Services)
      'CONSTRUCTION_REGISTRATION': '🏗️',
      'CONSTRUCTION_REGISTRATION_ADD': '➕',
      'CONSTRUCTION_REGISTRATION_EDIT': '✏️',
      'ENTRY_EXIT_REGISTRATION': '🚪',
      'ENTRY_EXIT_REGISTRATION_ADD': '➕',
      'ENTRY_EXIT_REGISTRATION_EDIT': '✏️',
      'MOVING_REGISTRATION': '📦',
      'MOVING_REGISTRATION_ADD': '➕',
      'MOVING_REGISTRATION_EDIT': '✏️',
      
      // CHĂM SÓC KHÁCH HÀNG (Customer Care)
      'FEEDBACK_LIST': '💬',
      'FEEDBACK_DETAILS': '📋',
      'FEEDBACK_CATEGORY_CONFIG': '📂',
      'FEEDBACK_STATISTICS': '📊',
      'CHAT': '💬',
      'CHAT_DETAILS': '📋',
      
      // TIỆN ÍCH HỆ THỐNG (System Utilities)
      'EMAIL_NOTIFICATIONS': '📧',
      'SEND_EMAIL': '📤',
      'EMAIL_LIST': '📋',
      'AUTOMATION_SETTINGS': '🤖',
      'SUPPORT': '🎧',
      
      // CÀI ĐẶT HỆ THỐNG (System Settings)
      'SYSTEM_SETTINGS': '⚙️',      
      'FUNCTION_CATEGORIES': '📁',    
      'FUNCTIONS': '🔧',
      'SETTINGS': '⚙️',
    };

    // Return the emoji icon if found, otherwise return a default icon
        return (
      <span className="text-lg">
        {iconMap[code] || '📄'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="fixed lg:static inset-y-0 left-0 z-50 lg:z-0 w-72 lg:w-80 bg-white shadow-lg border-r border-gray-200 h-screen flex flex-col transform transition-transform duration-300 ease-in-out translate-x-0">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed lg:static inset-y-0 left-0 z-50 lg:z-0 w-72 lg:w-80 bg-white shadow-lg border-r border-gray-200 h-screen flex flex-col transform transition-transform duration-300 ease-in-out translate-x-0">
        <div className="flex items-center justify-center h-full">
          <div className="text-red-500 text-center">
            <p>Error loading menu</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50
        w-72 lg:w-80 bg-white shadow-lg border-r border-gray-200 h-screen flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              <AppBrandMark logo={logo} size="sm" className="min-w-0" />
            </div>
            
            {/* Close button for mobile */}
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          {menu.map((section) => (
            <div key={section.id} className="mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2">
                {section.name || t(section.code)}
              </h3>
              <ul className="space-y-1">
                {section.children?.map(renderMenuItem)}
              </ul>
            </div>
          ))}
        </nav>

        {/* My Account + Logout */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0 space-y-2">
          {user && (
            <Link
              to="/my-account"
              onClick={onClose}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === '/my-account'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>{t('myAccount')}</span>
            </Link>
          )}
          <SignOutButton />
        </div>
      </div>
    </>
  );
}; 